import { useNegotiateBrandCost } from "@entities/brief/lib/hooks/useNegotiateBrandCost";
import { ToastService } from "@shared/lib/services";
import { IBriefCampaignBriefInfluencer } from "@shared/lib/types/briefCampains";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { usePopoverInputFocus } from "@shared/lib/utils/focusPopoverInput";
import { Button, Form, Input } from "@shared/ui";
import { FormInstance } from "antd";
import { Send } from "lucide-react";
import { useEffect } from "react";
import styles from "./styles.module.scss";

interface Props {
  campaignInfluencer: IBriefInfluencerV2 | IBriefCampaignBriefInfluencer;
  onclose: () => void;
  updateBriefInfluencer?: (id: string, data: IBriefInfluencerV2) => void;
  /** When 'popover', renders compact UI: title, input, -10%/-20% chips, Cancel/Save */
  variant?: "modal" | "popover";
}

export function NegotiateCommercials({
  campaignInfluencer,
  onclose,
  updateBriefInfluencer,
  variant = "modal",
}: Props) {
  const { isNegotiated, negotiate, isNegotiating } = useNegotiateBrandCost(
    campaignInfluencer.briefId,
    updateBriefInfluencer
  );
  const [form] = Form.useForm();
  const isPopover = variant === "popover";
  const offerAmountInputRef = usePopoverInputFocus(isPopover);

  const currency = "₹"; // Default currency, could be made configurable

  useEffect(() => {
    if (isNegotiated) {
      ToastService.success("Negotiated successfully");
      onclose();
      form.resetFields();
    }
  }, [isNegotiated]);

  const handleSubmit = (values: {
    offerAmount: string | number;
    message?: string;
  }) => {
    const amountNumber = Number(values.offerAmount);
    if (Number.isNaN(amountNumber)) {
      ToastService.error("Please enter a valid amount");
      return;
    }
    negotiate({
      briefInfluencerId: campaignInfluencer._id,
      targetBrandCost: amountNumber,
      remarks: values.message || "",
    });
  };

  const currentPrice = campaignInfluencer.brandCost?.cost ?? 0;

  const isInitialCostGiven = campaignInfluencer.brandCost?.cost !== undefined;

  const handleApplyDiscount = (percent: number, formInstance: FormInstance) => {
    const currentValue = formInstance.getFieldValue("offerAmount");
    const baseNum =
      currentValue !== undefined && currentValue !== ""
        ? Number(currentValue)
        : currentPrice;
    if (Number.isNaN(baseNum) || baseNum <= 0) return;
    const discounted = Math.round(baseNum * (1 - percent / 100));
    formInstance.setFieldValue("offerAmount", String(discounted));
  };

  return (
    <div className={styles.modalContent}>
      {isPopover && (
        <div className={styles.popoverTitle}>Set target commercial</div>
      )}
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        className={styles.form}
        requiredMark={false}
        initialValues={undefined}
      >
        {!isPopover && isInitialCostGiven && (
          <div className={styles.currentAskContainer}>
            <div className={styles.currentAskLabel}>Current Ask</div>
            <div className={styles.currentAskAmount}>
              {currency}
              {currentPrice}
            </div>
          </div>
        )}

        <Form.Item
          label={
            isPopover
              ? undefined
              : isInitialCostGiven
                ? "Your Counter Offer"
                : "Offer"
          }
          name="offerAmount"
          rules={[
            {
              validator: (_rule, value) => {
                const num = Number(value);
                if (Number.isNaN(num) || num <= 0) {
                  return Promise.reject("Amount must be greater than 0");
                }
                return Promise.resolve();
              },
            },
          ]}
          className={styles.formItem}
        >
          <Input
            ref={isPopover ? offerAmountInputRef : undefined}
            prefix={<span className={styles.currencyPrefix}>{currency}</span>}
            placeholder="Enter amount"
            className={styles.counterOfferInput}
            inputMode="decimal"
            autoFocus={!isPopover}
            allowClear
          />
        </Form.Item>

        {isPopover && (
          <div className={styles.discountChips}>
            <button
              type="button"
              className={styles.discountChip}
              onClick={() => handleApplyDiscount(10, form)}
            >
              -10%
            </button>
            <button
              type="button"
              className={styles.discountChip}
              onClick={() => handleApplyDiscount(20, form)}
            >
              -20%
            </button>
          </div>
        )}

        {/* {!isPopover && ( */}
        {/* <Form.Item
          label="Message (Optional)"
          name="message"
          className={styles.formItem}
        >
          <Input.TextArea
            placeholder={
              isInitialCostGiven
                ? "Add a note about why you are countering..."
                : "Add a note about why you are setting this target price..."
            }
            className={styles.messageTextarea}
            rows={2}
          />
        </Form.Item> */}
        {/* )} */}

        <div className={styles.formActions}>
          <Button type="text" onClick={onclose} size="small">
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            htmlType="submit"
            loading={isNegotiating}
            iconPosition={isPopover ? undefined : "end"}
            icon={isPopover ? undefined : <Send size={14} />}
          >
            {isPopover ? "Save" : "Send Offer"}
          </Button>
        </div>
      </Form>
    </div>
  );
}
